import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface Service {
  icon: string;
  title: string;
  description: string;
  price: string;
  priceNote: string;
  features: string[];
  featured?: boolean;
}

const SERVICES: Service[] = [
  {
    icon: "ri-layout-4-line",
    title: "Frontend Development",
    description: "Fast, responsive, pixel-perfect interfaces built with React and Next.js — the part your users actually see and feel.",
    price: "$300",
    priceNote: "starting price",
    features: [
      "React / Next.js (App Router)",
      "Responsive, mobile-first UI",
      "Tailwind CSS design system",
      "Smooth animations & interactions",
      "SEO-friendly markup",
    ],
  },
  {
    icon: "ri-stack-line",
    title: "Full-Stack Web App",
    description: "End-to-end product builds — frontend, backend, database, and auth — shipped as a single, cohesive application.",
    price: "$800",
    priceNote: "starting price",
    features: [
      "Next.js + Convex / Node backend",
      "Authentication & user roles",
      "Database schema & API design",
      "Admin dashboards & data views",
      "Deployment & production setup",
    ],
    featured: true,
  },
  {
    icon: "ri-tools-line",
    title: "Maintenance & Optimization",
    description: "Ongoing support for an existing site or app — bug fixes, performance tuning, and small feature additions.",
    price: "$150",
    priceNote: "per month",
    features: [
      "Bug fixes & production debugging",
      "Performance & load-time tuning",
      "SEO & Core Web Vitals audits",
      "Small feature updates",
      "Monthly check-ins & reporting",
    ],
  },
];

export default function DevServices() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current, { opacity: 0, y: 60, rotateX: -20, transformPerspective: 1000 }, {
        opacity: 1, y: 0, rotateX: 0, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: headerRef.current, start: "top 85%", once: true },
      });
      if (cardsRef.current) {
        const cards = cardsRef.current.querySelectorAll(".service-card");
        gsap.fromTo(cards, { opacity: 0, y: 50, scale: 0.95 }, {
          opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.12, ease: "power3.out",
          scrollTrigger: { trigger: cardsRef.current, start: "top 82%", once: true },
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
        perspective: "1200px",
      }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] rounded-full pointer-events-none" style={{ background: "radial-gradient(ellipse, rgba(34,197,94,0.05) 0%, transparent 70%)", filter: "blur(50px)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className="mb-14" style={{ transformStyle: "preserve-3d" }}>
          <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-3">What I Offer</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Services &amp;{" "}<span className="font-black text-green-600">Pricing</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-xs leading-relaxed">Straightforward packages — scoped and priced based on your project.</p>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        <div
          className="rounded-3xl p-6 md:p-10"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((service) => (
              <div
                key={service.title}
                className="service-card relative rounded-2xl overflow-hidden flex flex-col"
                style={{
                  background: service.featured ? "rgba(34,197,94,0.05)" : "rgba(255,255,255,0.03)",
                  border: service.featured ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(255,255,255,0.09)",
                }}
              >
                {service.featured && (
                  <div
                    className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl text-[10px] font-black font-mono tracking-wider uppercase"
                    style={{ background: "linear-gradient(90deg, rgba(34,197,94,0.9), rgba(16,185,129,0.7))", color: "#000" }}
                  >
                    Most Popular
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)" }}
                  >
                    <i className={`${service.icon} text-xl text-green-400`} />
                  </div>

                  <h3
                    className="text-white font-bold text-lg mb-2"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-white text-3xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {service.price}
                    </span>
                    <span className="text-gray-500 text-xs font-mono">{service.priceNote}</span>
                  </div>

                  <div className="h-px mb-6" style={{ background: "rgba(255,255,255,0.07)" }} />

                  <ul className="flex flex-col gap-2.5 mb-8 flex-1">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
                        <i className="ri-check-line text-green-400 text-base flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => scrollTo("contact")}
                    className="btn-3d w-full py-2.5 text-xs sm:text-sm font-mono"
                    style={
                      service.featured
                        ? { background: "linear-gradient(180deg, #22c55e 0%, #16a34a 100%)", color: "#000", fontWeight: 700, borderRadius: 8, boxShadow: "0 4px 0 #14532d, inset 0 1px 0 rgba(255,255,255,0.15)" }
                        : { background: "linear-gradient(180deg, #1d1d1d 0%, #141414 100%)", color: "#4ade80", fontWeight: 700, borderRadius: 8, border: "1px solid rgba(34,197,94,0.25)", boxShadow: "0 4px 0 #000, inset 0 1px 0 rgba(255,255,255,0.05)" }
                    }
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-send-plane-line text-xs" />
                      Get Started
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-xs font-mono text-center mt-8">
            Prices are starting points — final scope &amp; quote depend on project complexity.
          </p>
        </div>
      </div>
    </section>
  );
}